// What a host hands the guides views and the tour overlay (ADR 0060): the SDK's client for the app the page is
// about and the host's own affordances, injected. The views never import a host and never name an address: a
// capability the host does not provide is not offered (no "Build a guide" without a board, no app link without apps).
import type { GuidesClient } from '@cortex/sdk-guides-client';
import type { LucideIcon } from 'lucide-react';
import * as React from 'react';

export interface BuildRequestButtonProps {
  kind: string;
  label: string;
  testId: string;
  heading?: string;
  target?: string;
  look?: 'rail' | 'ghost' | 'primary';
  icon?: LucideIcon;
  className?: string;
}

export interface GuidesHost {
  /** The SDK's client, at where the host mounts the guides of the app open; null: no app is open (tours only). */
  client: GuidesClient | null;
  /** Names the app the client reaches (a tour's id, what the views reload on when it changes). */
  scope: string;
  /** The page's query string through the host's router (`?guide=`). */
  useSearchParams(): [
    URLSearchParams,
    (
      next: URLSearchParams | ((prev: URLSearchParams) => URLSearchParams),
      opts?: { replace?: boolean },
    ) => void,
  ];
  /** Client-side navigation of the host (a tour step's path, the guide's app link). */
  navigate(to: string): void;
  /** The host path of one of its apps (the guide's "explains" link); absent = no link. */
  appPath?: (app: string) => string;
  /**
   * A tour step's path as a guide wrote it (`<!-- tour: <anchor> <path> -->`, with the host's own placeholders),
   * as a route of the host; absent: the path as written.
   */
  routeOf?: (path: string) => string;
  /** The host's board: agents build new guides and changes. */
  buildRequest?: { Button: React.ComponentType<BuildRequestButtonProps> };
}

const Ctx = React.createContext<GuidesHost | null>(null);

export function GuidesHostProvider({ host, children }: { host: GuidesHost; children: React.ReactNode }) {
  return <Ctx.Provider value={host}>{children}</Ctx.Provider>;
}

export function useGuidesHost(): GuidesHost {
  const h = React.useContext(Ctx);
  if (!h) throw new Error('The guides views need a <GuidesHostProvider> (the host’s client and affordances)');
  return h;
}

/** The client of the app open: the views that need one are mounted only when the host has one. */
export function useGuidesClient(): GuidesClient {
  const c = useGuidesHost().client;
  if (!c) throw new Error('The guides views need an app open (the host’s client)');
  return c;
}

/** The message of a failed call: the API's own sentence, else its status. */
export function errorMessage(error: unknown, response?: Response): string {
  if (error && typeof error === 'object' && 'message' in error && typeof error.message === 'string')
    return error.message;
  return response ? `The API answered ${response.status}.` : 'The API is unreachable.';
}

/**
 * The query string of the page from `window.location`, for a host without a router: reading it and
 * replacing it with the History API. A host with a router passes its own hook instead.
 */
export function useLocationSearchParams(): ReturnType<GuidesHost['useSearchParams']> {
  const [params, setParams] = React.useState(() => new URLSearchParams(window.location.search));
  React.useEffect(() => {
    const on = () => setParams(new URLSearchParams(window.location.search));
    window.addEventListener('popstate', on);
    return () => window.removeEventListener('popstate', on);
  }, []);
  const set = React.useCallback<ReturnType<GuidesHost['useSearchParams']>[1]>((next, opts) => {
    setParams((prev) => {
      const p = typeof next === 'function' ? next(new URLSearchParams(prev)) : next;
      const url = `${window.location.pathname}${p.toString() ? `?${p}` : ''}${window.location.hash}`;
      if (opts?.replace) window.history.replaceState(null, '', url);
      else window.history.pushState(null, '', url);
      return new URLSearchParams(p);
    });
  }, []);
  return [params, set];
}
